// NotificationCenter (Task 13.6).
//
// The authoritative in-app notification list. Entries are shown newest-first
// (ordered via the domain `orderNotifications`, Req 19.1) with read/unread
// state, and an empty state with the exact string "No new notifications."
// (Req 29.4). Marking an entry read is a local view concern here.

import { useMemo, useState } from "react";
import { orderNotifications, type Notification } from "@sharebite/domain";
import { AsyncSection } from "../../components/AsyncSection";
import * as api from "../../lib/api";

function describe(type: Notification["type"]): string {
  switch (type) {
    case "Reserved":
      return "Your donation was reserved.";
    case "Completed":
      return "A donation was completed.";
    case "Expired":
      return "A donation expired.";
    case "NewMatch":
      return "A new matching donation is available.";
    case "ReservationCancelled":
      return "A reservation was cancelled.";
    case "NewRegistration":
      return "A new registration was submitted.";
    case "VerificationPending":
      return "A verification is pending.";
    case "VerificationOutcome":
      return "Your verification status changed.";
    case "BadgeAwarded":
      return "You earned a new badge!";
    default:
      return "Notification";
  }
}

function NotificationList({
  notifications,
}: {
  notifications: Notification[];
}): JSX.Element {
  const ordered = useMemo(
    () => orderNotifications(notifications),
    [notifications],
  );
  const [read, setRead] = useState<Set<string>>(
    () => new Set(notifications.filter((n) => n.read).map((n) => n.notificationId)),
  );

  return (
    <div>
      {ordered.map((n) => {
        const isRead = read.has(n.notificationId);
        return (
          <div
            key={n.notificationId}
            className="sb-list-item"
            data-read={isRead}
          >
            <div className="sb-row">
              {!isRead && <span className="sb-pill">Unread</span>}
              <span>{describe(n.type)}</span>
              <span className="sb-spacer" />
              <span className="sb-muted">
                {new Date(n.createdAt).toLocaleString()}
              </span>
              {!isRead && (
                <button
                  type="button"
                  className="sb-btn sb-btn--secondary"
                  onClick={() =>
                    setRead((prev) => new Set(prev).add(n.notificationId))
                  }
                >
                  Mark read
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function NotificationCenter({ uid }: { uid: string }): JSX.Element {
  return (
    <div className="sb-main">
      <AsyncSection
        title="Notifications"
        loader={() => api.listNotifications(uid)}
        emptyText="No new notifications."
        isEmpty={(n: Notification[]) => n.length === 0}
      >
        {(notifications: Notification[]) => (
          <NotificationList notifications={notifications} />
        )}
      </AsyncSection>
    </div>
  );
}
